import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { DataGrid, GridFilterInputDate } from "@mui/x-data-grid";
import { Container, Typography, Box, CircularProgress, Alert, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, MenuItem, Select, InputLabel, FormControl, Snackbar, Chip, Collapse } from "@mui/material";
import { API_ENDPOINTS, API_BASE_URL } from "../config/api.config";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { format } from 'date-fns';
import { UserContext } from '../components/MiniDrawer';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Tooltip as MuiTooltip } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { parseLocalDate, isFutureDay } from '../utils/dateHelpers';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { getAbsences, deleteAbsence } from '../utils/absenceService';
import { FORECAST_CONFIG } from '../config/local.config';
import { upsertForecastCell, hoursToMinutes, minutesToHours } from '../utils/forecastService';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

function getColumnsFromData(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  // Colonne principali flat secondo il nuovo tracciato
  return [
    { field: 'project_name', headerName: 'Project', width: 180 },
    { field: 'ticket_id', headerName: 'Ticket ID', width: 140 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130,
      type: 'date',
      filterable: true,
      valueFormatter: (value) => {
        if (!value) return '';
        // Fix timezone: parseLocalDate interpreta la data come locale
        const date = parseLocalDate(value);
        return date.toLocaleDateString('en-GB'); // Format as DD/MM/YYYY
      },
      filterOperators: [
        {
          label: 'Equals',
          value: 'equals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return value === filterItem.value;
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'After',
          value: 'after',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              // Fix timezone: parseLocalDate interpreta la data come locale
              return parseLocalDate(value) > parseLocalDate(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'Before',
          value: 'before',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              // Fix timezone: parseLocalDate interpreta la data come locale
              return parseLocalDate(value) < parseLocalDate(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
      ],
    },
    { 
      field: 'effort', 
      headerName: 'Effort', 
      width: 100,
      valueFormatter: (value) => {
        if (!value) return '';
        const hours = Math.round((Number(value) / 60) * 100) / 100;
        return `${hours} h`;
      }
    },
    { field: 'activity_name', headerName: 'Activity', width: 180 },
    { field: 'status_name', headerName: 'Status', width: 140 },
    { field: 'effort_type_name', headerName: 'Effort Type', width: 140 },
    { field: 'user_name', headerName: 'User', width: 180 },
    { field: 'description', headerName: 'Description', width: 250 },
  ];
}

function parseAssignedProjects(projectUsersPayload) {
  return (projectUsersPayload?.user_projects_with_references || [])
    .filter((item) => item.project?.active !== false)
    .map((item) => ({
      project_id: item.project.id,
      project_name: item.project.name || 'Unknown Project',
    }));
}

function mergeMonthlyPivot(summaryItems, assignedProjects) {
  const byProject = {};
  const meta = {};

  assignedProjects.forEach(({ project_id, project_name }) => {
    byProject[project_name] = {};
    meta[project_name] = { project_id, project_name };
  });

  (summaryItems || []).forEach((item) => {
    const project = item.project_name || (item.project && item.project.name) || 'Unknown Project';
    const day = parseLocalDate(item.work_date).getDate();
    if (item.project_id) {
      meta[project] = { project_id: item.project_id, project_name: project };
    }
    if (!byProject[project]) byProject[project] = {};
    const actual = Number(item.actual_effort ?? item.total_effort) || 0;
    const forecast = Number(item.forecast_effort) || 0;
    const existing = byProject[project][day];
    if (existing) {
      byProject[project][day] = {
        ...existing,
        actual_effort: (Number(existing.actual_effort) || 0) + actual,
        forecast_effort: (Number(existing.forecast_effort) || 0) + forecast,
      };
    } else {
      byProject[project][day] = {
        actual_effort: actual,
        forecast_effort: forecast,
        project_id: item.project_id,
      };
    }
  });

  return { byProject, meta };
}

function forecastCellKey(rowKey, day) {
  return `${rowKey}|${day}`;
}

const FORECAST_HELP_DISMISSED_KEY = 'runtimesheet_forecast_help_dismissed';

function ForecastLegendItem({ bg, border, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: bg || 'white', border: border || '1px solid #e0e0e0', flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

function buildForecastDraftsFromPivot(pivotData, filterYear, filterMonth, daysInMonth) {
  const drafts = {};
  Object.keys(pivotData).forEach((rowKey) => {
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (!isFutureDay(dateStr)) continue;
      const obj = pivotData[rowKey]?.[d];
      drafts[forecastCellKey(rowKey, d)] = obj?.forecast_effort
        ? String(minutesToHours(obj.forecast_effort))
        : '';
    }
  });
  return drafts;
}

export default function MonthlyUserView() {
  const { selectedUser } = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({});
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [effortTypes, setEffortTypes] = useState([]);
  const [absenceDays, setAbsenceDays] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [forecastMode, setForecastMode] = useState(searchParams.get('mode') === 'forecast');
  const [forecastDrafts, setForecastDrafts] = useState({});
  const [forecastBaseline, setForecastBaseline] = useState({});
  const [savingForecasts, setSavingForecasts] = useState(false);
  const [showForecastGuide, setShowForecastGuide] = useState(
    () => localStorage.getItem(FORECAST_HELP_DISMISSED_KEY) !== '1'
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);
  const [pendingUnsavedAction, setPendingUnsavedAction] = useState(null);
  const [projectMeta, setProjectMeta] = useState({});

  // Filtri per mese/anno - legge dai parametri URL se presenti
  const now = new Date();
  const urlMonth = searchParams.get('month');
  const urlYear = searchParams.get('year');
  const [filterMonth, setFilterMonth] = useState(
    urlMonth ? parseInt(urlMonth) : now.getMonth() + 1
  ); // 1-12
  const [filterYear, setFilterYear] = useState(
    urlYear ? parseInt(urlYear) : now.getFullYear()
  );
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const yearOptions = [];
  for (let y = 2020; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  useEffect(() => {
    setForecastMode(searchParams.get('mode') === 'forecast');
  }, [searchParams]);

  // Aggiorna i filtri quando cambiano i parametri URL
  useEffect(() => {
    const urlMonth = searchParams.get('month');
    const urlYear = searchParams.get('year');
    if (urlMonth) setFilterMonth(parseInt(urlMonth));
    if (urlYear) setFilterYear(parseInt(urlYear));
  }, [searchParams]);

  // Sposto fetchWorklogs fuori da useEffect
  const fetchWorklogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = selectedUser === 'ALL'
        ? API_ENDPOINTS.WORKLOGS
        : `${API_ENDPOINTS.WORKLOGS}?user_id=${selectedUser}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Error fetching data");
      const data = await response.json();
      const arr = data.worklogs || [];
      setRows((selectedUser === 'ALL' ? arr : arr.filter(row => String(row.user_id || row.user?.id) === String(selectedUser))).map((row, idx) => ({ id: row.id || idx, ...row })));
      setColumns(getColumnsFromData(arr));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedUser) return;
    fetchWorklogs();
  }, [selectedUser]);

  // Modifico il filtro dei dati
  const filteredRows = rows.filter(row => {
    let match = true;
    if (row.date) {
      // Fix timezone: parseLocalDate interpreta la data come locale
      const d = parseLocalDate(row.date);
      match = match && (d.getMonth() + 1 === Number(filterMonth)) && (d.getFullYear() === Number(filterYear));
    }
    return match;
  });

  // Ricava i campi editabili (escludi id, created_at, updated_at)
  const getEditableFields = () => {
    if (!columns.length) return [];
    return columns.filter(col => !['id', 'created_at', 'updated_at'].includes(col.field));
  };

  // Gestione apertura dialog
  const handleOpenDialog = (row = null) => {
    setEditingRow(row);
    setFormData(row ? { ...row } : { user: selectedUser });
    setOpenDialog(true);
    // Fetch users
    fetch(API_ENDPOINTS.USERS)
      .then(res => res.json())
      .then(data => {
        let usersArr = data.users || [];
        setUsers(Array.isArray(usersArr) ? usersArr : []);
      })
      .catch(() => setUsers([]));
    // Fetch activities
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        let arr = data.activities || [];
        setActivities(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setActivities([]));
    // Fetch status
    fetch(API_ENDPOINTS.STATUS)
      .then(res => res.json())
      .then(data => {
        let arr = data.status || [];
        setStatusList(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setStatusList([]));
    // Fetch effort types
    fetch(API_ENDPOINTS.EFFORT_TYPES)
      .then(res => res.json())
      .then(data => {
        let arr = data.effort_types || [];
        setEffortTypes(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setEffortTypes([]));
  };

  // Rimuovo la funzione handleExportExcel

  // Azioni e colonne
  const statusCol = columns.find(col => col.field === 'status_name');
  const otherCols = columns.filter(col => col.field !== 'status_name');
  const actionsColumn = {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    renderCell: (params) => (
      <Box>
        {params.row.status_name === 'Approved' ? (
          <Tooltip title="Blocked">
            <span>
              <IconButton disabled style={{ color: '#bdbdbd' }}>
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Edit">
            <span>
              <IconButton onClick={() => handleOpenDialog(params.row)}>
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {params.row.status_name === 'Approved' ? (
          <Tooltip title="Blocked">
            <span>
              <IconButton disabled style={{ color: '#bdbdbd' }}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Delete">
            <span>
              <IconButton onClick={() => handleDelete(params.row.id)}>
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    )
  };
  const columnsWithActions = statusCol
    ? [actionsColumn, ...otherCols, statusCol]
    : [actionsColumn, ...otherCols];

  // Tooltip per la colonna Description
  const columnsWithDescriptionTooltip = columnsWithActions.map(col => {
    if (col.field === 'description') {
      return {
        ...col,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} placement="top" arrow>
            <span style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              width: '100%'
            }}>{params.value}</span>
          </Tooltip>
        )
      };
    }
    return col;
  });

  // Dialog helpers
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRow(null);
    setFormData({});
  };
  const handleFormChange = (e, field) => {
    if (e && e.target) {
      setFormData({ ...formData, [field]: e.target.value });
    }
  };
  const handleSave = async () => {};
  const handleDelete = async (id) => {};

  // Stato per la tabella pivot
  const [pivotData, setPivotData] = useState([]);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [pivotError, setPivotError] = useState(null);
  const [pivotMode, setPivotMode] = useState('user'); // 'user' o 'project'
  // Stato per dettagli celle
  const [pivotDetails, setPivotDetails] = useState({});

  // Calcola giorni del mese selezionato
  const daysInMonth = useMemo(() => {
    return new Date(filterYear, filterMonth, 0).getDate();
  }, [filterYear, filterMonth]);

  const todayDay = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (today.getFullYear() === filterYear && today.getMonth() + 1 === filterMonth) {
      return today.getDate();
    }
    return null;
  }, [filterYear, filterMonth]);

  /** Evidenzia la colonna "oggi" con bordi verticali continui (no righe orizzontali ripetute per riga). */
  const getTodayColumnSx = (isToday, { onGreenHeader = false } = {}) => {
    if (!isToday) return {};
    if (onGreenHeader) {
      return {
        fontWeight: 700,
        borderLeft: '2px solid rgba(255,255,255,0.85)',
        borderRight: '2px solid rgba(255,255,255,0.85)',
      };
    }
    return {
      borderLeft: '2px solid #5ab031',
      borderRight: '2px solid #5ab031',
    };
  };

  const futureEditableDaysCount = useMemo(() => {
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (isFutureDay(dateStr)) count += 1;
    }
    return count;
  }, [filterYear, filterMonth, daysInMonth]);

  useEffect(() => {
    if (forecastMode && localStorage.getItem(FORECAST_HELP_DISMISSED_KEY) !== '1') {
      setShowForecastGuide(true);
    }
  }, [forecastMode]);

  useEffect(() => {
    if (!forecastMode) {
      setForecastDrafts({});
      setForecastBaseline({});
      return;
    }
    const drafts = buildForecastDraftsFromPivot(pivotData, filterYear, filterMonth, daysInMonth);
    setForecastDrafts(drafts);
    setForecastBaseline(drafts);
  }, [forecastMode, pivotData, filterYear, filterMonth, daysInMonth]);

  // Dimensioni fisse per tabella responsive con scroll orizzontale
  const tableDimensions = useMemo(() => {
    const firstColWidth = 140; // px
    const dayColWidth = 45; // px per ciascun giorno
    const totalColWidth = 60; // px
    const minWidth = firstColWidth + daysInMonth * dayColWidth + totalColWidth;
    return { firstColWidth, dayColWidth, totalColWidth, minWidth };
  }, [daysInMonth]);

  // Calcola i nomi dei giorni della settimana per il mese selezionato
  const weekDays = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      return date.toLocaleDateString('en-GB', { weekday: 'short' }).charAt(0);
    });
  }, [filterYear, filterMonth, daysInMonth]);

  // Calcola indici dei weekend per il mese selezionato
  const weekendIndexes = useMemo(() => {
    return weekDays.map((wd, i) => {
      const date = new Date(filterYear, filterMonth - 1, i + 1);
      const day = date.getDay(); // 0=Sunday, 6=Saturday
      return (day === 0 || day === 6);
    });
  }, [weekDays, filterYear, filterMonth]);

  useEffect(() => {
    if (!selectedUser || selectedUser === 'ALL') { setAbsenceDays([]); return; }
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    getAbsences(selectedUser, yearMonth).then(setAbsenceDays).catch(() => setAbsenceDays([]));
  }, [selectedUser, filterMonth, filterYear]);

  // Fetch pivot data ogni volta che cambia mese/anno o utente
  useEffect(() => {
    setPivotLoading(true);
    setPivotError(null);
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const userId = selectedUser === 'ALL' ? '' : selectedUser;
    if (!userId) {
      setPivotData({});
      setPivotLoading(false);
      return;
    }
    const summaryUrl = `${API_BASE_URL}/monthly_worklog_summary_user_project?user_id=${userId}&year_month=${yearMonth}&include_forecast=true`;
    const projectsUrl = `${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}`;
    Promise.all([
      fetch(summaryUrl).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
      fetch(projectsUrl).then((res) => (res.ok ? res.json() : { user_projects_with_references: [] })),
    ])
      .then(([summaryData, projectUsersData]) => {
        const assignedProjects = parseAssignedProjects(projectUsersData);
        const { byProject, meta } = mergeMonthlyPivot(summaryData.data, assignedProjects);
        setProjectMeta(meta);
        setPivotData(byProject);
      })
      .catch(() => setPivotError('Error loading data'))
      .finally(() => setPivotLoading(false));
  }, [filterMonth, filterYear, selectedUser]);

  const getForecastMinutesForCell = (rowKey, day, obj) => {
    const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (forecastMode && isFutureDay(dateStr)) {
      const draft = forecastDrafts[forecastCellKey(rowKey, day)];
      if (draft !== undefined && draft !== '') return hoursToMinutes(draft);
      if (draft === '') return 0;
    }
    if (isFutureDay(dateStr)) return Number(obj?.forecast_effort) || 0;
    return Number(obj?.actual_effort) || 0;
  };

  const hasPendingForecastChanges = useMemo(() => (
    Object.keys({ ...forecastBaseline, ...forecastDrafts }).some((key) => (
      (forecastDrafts[key] ?? '') !== (forecastBaseline[key] ?? '')
    ))
  ), [forecastDrafts, forecastBaseline]);

  const pendingForecastChangesCount = useMemo(() => (
    Object.keys({ ...forecastBaseline, ...forecastDrafts }).filter((key) => (
      (forecastDrafts[key] ?? '') !== (forecastBaseline[key] ?? '')
    )).length
  ), [forecastDrafts, forecastBaseline]);

  const runWithUnsavedGuard = useCallback((action) => {
    if (forecastMode && hasPendingForecastChanges) {
      setPendingUnsavedAction(() => action);
      setUnsavedConfirmOpen(true);
      return;
    }
    action();
  }, [forecastMode, hasPendingForecastChanges]);

  const dismissForecastGuide = () => {
    setShowForecastGuide(false);
    localStorage.setItem(FORECAST_HELP_DISMISSED_KEY, '1');
  };

  const handleForecastModeToggle = () => {
    if (forecastMode) {
      runWithUnsavedGuard(() => setForecastMode(false));
      return;
    }
    setForecastMode(true);
  };

  const applyFilterMonth = (month) => runWithUnsavedGuard(() => setFilterMonth(month));
  const applyFilterYear = (year) => runWithUnsavedGuard(() => setFilterYear(year));
  const applyFilterReset = () => runWithUnsavedGuard(() => {
    setFilterMonth(now.getMonth() + 1);
    setFilterYear(now.getFullYear());
  });

  const shiftMonth = (delta) => runWithUnsavedGuard(() => {
    let m = filterMonth + delta;
    let y = filterYear;
    if (m < 1) {
      m = 12;
      y -= 1;
    } else if (m > 12) {
      m = 1;
      y += 1;
    }
    setFilterMonth(m);
    setFilterYear(y);
  });

  const handleConfirmDiscardUnsaved = () => {
    setForecastDrafts({ ...forecastBaseline });
    setUnsavedConfirmOpen(false);
    const action = pendingUnsavedAction;
    setPendingUnsavedAction(null);
    action?.();
  };

  // Calcola totali per riga e per giorno (basati sui dati filtrati, convertiti da minuti a ore)
  const rowTotals = useMemo(() => {
    const totals = {};
    Object.entries(pivotData).forEach(([rowKey, days]) => {
      let sum = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        sum += getForecastMinutesForCell(rowKey, d, days[d]);
      }
      totals[rowKey] = Math.round((sum / 60) * 100) / 100;
    });
    return totals;
  }, [pivotData, daysInMonth, filterYear, filterMonth, forecastMode, forecastDrafts]);
  const dayTotals = useMemo(() => {
    const totals = {};
    for (let d = 1; d <= daysInMonth; d++) {
      totals[d] = Math.round((Object.entries(pivotData).reduce((sum, [rowKey, days]) => (
        sum + getForecastMinutesForCell(rowKey, d, days[d])
      ), 0) / 60) * 100) / 100;
    }
    return totals;
  }, [pivotData, daysInMonth, filterYear, filterMonth, forecastMode, forecastDrafts]);
  const grandTotal = useMemo(() => Math.round(Object.values(rowTotals).reduce((sum, v) => sum + v, 0) * 100) / 100, [rowTotals]);

  const reloadPivot = () => {
    const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const userId = selectedUser === 'ALL' ? '' : selectedUser;
    if (!userId) return Promise.resolve();
    setPivotLoading(true);
    return Promise.all([
      fetch(`${API_BASE_URL}/monthly_worklog_summary_user_project?user_id=${userId}&year_month=${yearMonth}&include_forecast=true`)
        .then((res) => (res.ok ? res.json() : Promise.reject())),
      fetch(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}`)
        .then((res) => (res.ok ? res.json() : { user_projects_with_references: [] })),
    ])
      .then(([summaryData, projectUsersData]) => {
        const assignedProjects = parseAssignedProjects(projectUsersData);
        const { byProject, meta } = mergeMonthlyPivot(summaryData.data, assignedProjects);
        setProjectMeta(meta);
        setPivotData(byProject);
      })
      .catch(() => setPivotError('Error loading data'))
      .finally(() => setPivotLoading(false));
  };

  const handleSaveAllForecasts = useCallback(async () => {
    if (!selectedUser || selectedUser === 'ALL' || savingForecasts) return;

    const changes = [];
    Object.keys({ ...forecastBaseline, ...forecastDrafts }).forEach((key) => {
      const draft = forecastDrafts[key] ?? '';
      const baseline = forecastBaseline[key] ?? '';
      if (draft === baseline) return;

      const sep = key.lastIndexOf('|');
      const rowKey = key.slice(0, sep);
      const day = Number(key.slice(sep + 1));
      const projectId = projectMeta[rowKey]?.project_id || pivotData[rowKey]?.[day]?.project_id;
      if (!projectId) return;

      const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      if (!isFutureDay(dateStr)) return;

      changes.push({
        projectId: Number(projectId),
        date: dateStr,
        effortMinutes: hoursToMinutes(draft),
      });
    });

    if (changes.length === 0) return;

    setSavingForecasts(true);
    try {
      await Promise.all(changes.map((cell) => upsertForecastCell({
        userId: Number(selectedUser),
        projectId: cell.projectId,
        date: cell.date,
        effortMinutes: cell.effortMinutes,
      })));
      await reloadPivot();
      setSnackbar({
        open: true,
        message: `Forecast saved (${changes.length} cell${changes.length === 1 ? '' : 's'})`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error saving forecast',
        severity: 'error',
      });
    } finally {
      setSavingForecasts(false);
    }
  }, [
    selectedUser,
    savingForecasts,
    forecastBaseline,
    forecastDrafts,
    projectMeta,
    pivotData,
    filterYear,
    filterMonth,
  ]);

  const handleDiscardForecastDrafts = () => {
    setForecastDrafts({ ...forecastBaseline });
  };

  const updateForecastDraft = (rowKey, day, value) => {
    const key = forecastCellKey(rowKey, day);
    setForecastDrafts((prev) => ({ ...prev, [key]: value.replace(/[^0-9.,]/g, '') }));
  };

  const renderPivotCellContent = (obj, dateStr, rowKey, day, projectId) => {
    const future = isFutureDay(dateStr);
    const actualH = obj ? minutesToHours(obj.actual_effort) : '';
    const forecastH = obj ? minutesToHours(obj.forecast_effort) : '';

    if (forecastMode && future && selectedUser !== 'ALL' && projectId) {
      const key = forecastCellKey(rowKey, day);
      const draft = forecastDrafts[key] ?? '';
      const isDirty = draft !== (forecastBaseline[key] ?? '');
      return (
        <TextField
          size="small"
          value={draft}
          onChange={(e) => updateForecastDraft(rowKey, day, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="·"
          inputProps={{
            'aria-label': `Forecast hours for ${rowKey} on ${dateStr}`,
            inputMode: 'decimal',
          }}
          sx={{
            width: 42,
            '& input::placeholder': { opacity: 0.35, fontSize: '0.85rem' },
            '& input': {
              p: 0.25,
              fontSize: '0.7rem',
              textAlign: 'center',
              color: isDirty ? FORECAST_CONFIG.forecastCellColor : 'inherit',
              fontWeight: 700,
            },
            '& .MuiOutlinedInput-root': {
              bgcolor: 'transparent',
              '& fieldset': {
                borderColor: isDirty ? FORECAST_CONFIG.forecastCellColor : 'rgba(0,0,0,0.12)',
              },
              '&:hover fieldset': {
                borderColor: isDirty ? FORECAST_CONFIG.forecastCellColor : 'rgba(0,0,0,0.25)',
              },
              '&.Mui-focused fieldset': {
                borderColor: FORECAST_CONFIG.forecastCellColor,
                borderWidth: 1,
              },
            },
          }}
        />
      );
    }

    if (future) {
      return forecastH ? (
        <span style={{ color: FORECAST_CONFIG.forecastCellColor, fontWeight: 700 }}>{forecastH}</span>
      ) : '';
    }
    return actualH || '';
  };

  const getCellSx = (dateStr, absEntry, isWeekend, rowKey, day, projectId, isToday = false) => {
    const future = isFutureDay(dateStr);
    const editable = forecastMode && future && selectedUser !== 'ALL' && projectId;

    let bgcolor = absEntry
      ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important')
      : isWeekend ? 'rgba(55,55,55,0.08)' : 'white';

    if (editable && !absEntry && !isWeekend) {
      bgcolor = FORECAST_CONFIG.editableHighlightBg;
    } else if (editable && !absEntry && isWeekend) {
      bgcolor = 'rgba(55,55,55,0.08)';
    }

    return {
      textAlign: 'center',
      bgcolor,
      color: '#374151',
      fontSize: '0.7rem',
      p: 0.5,
      border: '1px solid #f3f3f3',
      fontWeight: 'normal',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontVariantNumeric: 'tabular-nums',
      cursor: editable ? 'text' : 'pointer',
      ...getTodayColumnSx(isToday),
      '&:hover': {
        bgcolor: editable
          ? (absEntry
            ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important')
            : (isWeekend ? 'rgba(55,55,55,0.14)' : '#ffe0b2'))
          : absEntry
            ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important')
            : '#aace7c78',
      },
    };
  };

  const handleCellClick = (dateStr, rowKey, day, projectId) => {
    if (forecastMode && isFutureDay(dateStr) && selectedUser !== 'ALL' && projectId) return;
    if (!forecastMode) navigate(`/daily-tracking?date=${dateStr}`);
  };

  // Funzione per esportare i worklog in Excel
  const handleExportExcel = async () => {
    try {
      // Costruisce l'URL per il fetch dei worklog
      const url = selectedUser === 'ALL'
        ? API_ENDPOINTS.WORKLOGS
        : `${API_ENDPOINTS.WORKLOGS}?user_id=${selectedUser}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error("Error fetching data for export");
      const data = await response.json();
      const allWorklogs = data.worklogs || [];
      
      // Filtra i worklog per il mese/anno selezionato
      const filteredWorklogs = allWorklogs.filter(row => {
        if (!row.date) return false;
        // Fix timezone: parseLocalDate interpreta la data come locale
        const d = parseLocalDate(row.date);
        return (d.getMonth() + 1 === Number(filterMonth)) && (d.getFullYear() === Number(filterYear));
      });

      if (filteredWorklogs.length === 0) {
        alert('No data to export for the selected period');
        return;
      }

      // Prepara i dati per l'Excel
      const excelData = filteredWorklogs.map(row => ({
        'Project': row.project_name || '',
        'Ticket ID': row.ticket_id || '',
        'Date': row.date ? parseLocalDate(row.date).toLocaleDateString('en-GB') : '',
        'Effort in minutes': row.effort || '',
        'Activity': row.activity_name || '',
        'Status': row.status_name || '',
        'Effort Type': row.effort_type_name || '',
        'User': row.user_name || '',
        'Description': row.description || ''
      }));

      // Crea il workbook e il worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Aggiunge il worksheet al workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Worklogs');
      
      // Genera il file Excel
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Genera il nome del file
      const monthName = monthNames[filterMonth - 1];
      const fileName = `Worklogs_${monthName}_${filterYear}.xlsx`;
      
      // Scarica il file
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting data to Excel');
    }
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>
            {forecastMode ? 'Manage Forecast' : 'Monthly User View'}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              color="inherit"
              startIcon={forecastMode ? <CloseIcon /> : <CalendarMonthIcon />}
              onClick={handleForecastModeToggle}
              disabled={selectedUser === 'ALL'}
              sx={{
                color: forecastMode ? '#374151' : FORECAST_CONFIG.forecastCellColor,
                borderColor: forecastMode ? '#bdbdbd' : FORECAST_CONFIG.forecastCellColor,
                borderWidth: 1,
                borderStyle: 'solid',
                bgcolor: 'transparent',
                '& .MuiSvgIcon-root': { color: 'inherit' },
                '&:hover': {
                  bgcolor: forecastMode ? 'rgba(0,0,0,0.04)' : FORECAST_CONFIG.forecastCellBg,
                  borderColor: forecastMode ? '#9e9e9e' : FORECAST_CONFIG.forecastCellColor,
                  color: forecastMode ? '#212121' : FORECAST_CONFIG.forecastCellColor,
                },
              }}
            >
              {forecastMode ? 'Exit Forecast' : 'Manage Forecast'}
            </Button>
            {forecastMode && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={savingForecasts ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveAllForecasts}
                  disabled={!hasPendingForecastChanges || savingForecasts || pivotLoading}
                >
                  {savingForecasts
                    ? 'Saving...'
                    : `Save Forecast${pendingForecastChangesCount ? ` (${pendingForecastChangesCount})` : ''}`}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDiscardForecastDrafts}
                  disabled={!hasPendingForecastChanges || savingForecasts}
                >
                  Discard
                </Button>
                <Tooltip title="Show guide">
                  <IconButton size="small" onClick={() => setShowForecastGuide(true)} aria-label="Show forecast guide">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {!forecastMode && (
              <Button variant="outlined" color="primary" onClick={handleExportExcel} startIcon={<DownloadIcon />}>
                Excel
              </Button>
            )}
          </Box>
        </Box>
        {forecastMode && (
          <Collapse in={showForecastGuide}>
            <Alert
              severity="info"
              icon={<LightbulbOutlinedIcon fontSize="inherit" />}
              sx={{ mb: 2 }}
              action={(
                <IconButton size="small" onClick={dismissForecastGuide} aria-label="Dismiss guide">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                How forecast works
              </Typography>
              <Typography variant="body2" component="ol" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
                <li>Type expected hours in <strong>future day</strong> cells (one row per project).</li>
                <li>Check row and column totals — they update as you type.</li>
                <li>Press <strong>Save Forecast</strong> when finished. Nothing is saved until then.</li>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <ForecastLegendItem bg="white" border="1px solid #e0e0e0" label="Past days — actual hours (read-only)" />
                <ForecastLegendItem bg={FORECAST_CONFIG.editableHighlightBg} label="Future weekdays — editable (orange highlight)" />
                <ForecastLegendItem bg="white" border={`2px solid ${FORECAST_CONFIG.forecastCellColor}`} label="Forecast — bold blue text" />
                <ForecastLegendItem bg="#ffcdd2" label="Full absence" />
                <ForecastLegendItem bg="#fff9c4" label="Partial absence" />
              </Box>
            </Alert>
          </Collapse>
        )}
        {forecastMode && futureEditableDaysCount === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This month has no future days left. Switch month/year to plan ahead, or turn off forecast mode to review actuals.
          </Alert>
        )}
        {/* Filtri mese e anno */}
        <Box 
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <Tooltip title="Previous month">
            <IconButton size="small" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="filter-month-label">Month</InputLabel>
            <Select
              labelId="filter-month-label"
              value={filterMonth}
              label="Month"
              onChange={e => applyFilterMonth(Number(e.target.value))}
            >
              {monthNames.map((name, idx) => (
                <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel id="filter-year-label">Year</InputLabel>
            <Select
              labelId="filter-year-label"
              value={filterYear}
              label="Year"
              onChange={e => applyFilterYear(Number(e.target.value))}
            >
              {yearOptions.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Next month">
            <IconButton size="small" onClick={() => shiftMonth(1)} aria-label="Next month">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" size="small" onClick={applyFilterReset}>Reset</Button>
        </Box>
        {!forecastMode && !pivotLoading && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <ForecastLegendItem bg="white" border="1px solid #e0e0e0" label="Click a cell → daily tracking" />
            <ForecastLegendItem bg="rgba(55,55,55,0.08)" label="Weekend" />
            <ForecastLegendItem bg="white" border="2px solid #5ab031" label="Today (vertical borders)" />
            <ForecastLegendItem bg="#ffcdd2" label="Full absence" />
            <ForecastLegendItem bg="#fff9c4" label="Partial absence" />
          </Box>
        )}
        {absenceDays.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff8f5', borderRadius: 2, border: '1px solid #ffccbc', display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
              <EventBusyIcon sx={{ color: '#e53935', fontSize: '1.1rem' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#bf360c' }}>Absence days:</Typography>
            </Box>
            {absenceDays.sort((a, b) => a.date.localeCompare(b.date)).map(v => (
              <Chip
                key={v.id}
                label={`${parseLocalDate(v.date).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' })} (${v.full_day ? 'full' : 'partial'})`}
                size="small"
                sx={{ bgcolor: v.full_day ? '#ef9a9a' : '#fff176', color: v.full_day ? '#b71c1c' : '#f57f17', fontWeight: 500, '& .MuiChip-deleteIcon': { color: v.full_day ? 'rgba(183,28,28,0.6)' : 'rgba(245,127,23,0.6)', '&:hover': { color: v.full_day ? '#b71c1c' : '#f57f17' } } }}
                onDelete={async () => {
                  await deleteAbsence(v.id);
                  const yearMonth = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
                  getAbsences(selectedUser, yearMonth).then(setAbsenceDays).catch(() => setAbsenceDays([]));
                }}
              />
            ))}
          </Box>
        )}
        {pivotLoading ? (
          <CircularProgress />
        ) : pivotError ? (
          <Alert severity="error">{pivotError}</Alert>
        ) : (Object.keys(pivotData).length === 0 && absenceDays.length === 0) ? (
          <Alert severity="info">No worklogs or absences found for the selected user and date period.</Alert>
        ) : (
          <TableContainer component={Paper} sx={{ 
            borderRadius: 2, 
            bgcolor: 'white', 
            p: 0, 
            maxWidth: '100vw', 
            overflowX: 'auto', 
            boxShadow: 'none', 
            border: '1px solid #f3f3f3'
          }}>
            <Table size="small" sx={{ 
              minWidth: tableDimensions.minWidth, 
              tableLayout: 'fixed', 
              fontSize: '0.85rem', 
              width: '100%', 
              bgcolor: 'white'
            }}>
              <colgroup>
                <col style={{ width: tableDimensions.firstColWidth }} />
                {Array.from({ length: daysInMonth }).map((_, i) => (
                  <col key={i} style={{ width: tableDimensions.dayColWidth }} />
                ))}
                <col style={{ width: tableDimensions.totalColWidth }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell
                    className="pivot-user-col"
                    sx={{
                      bgcolor: '#5ab031',
                      border: 'none',
                      p: 0,
                      minWidth: 120,
                      maxWidth: 160,
                      width: 140,
                      color: 'white',
                      fontWeight: 'normal',
                      borderRight: '0px solid #e0e0e0',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}
                    rowSpan={2}
                  >
                    Project
                  </TableCell>
                  {weekDays.map((wd, i) => {
                    const isWeekend = weekendIndexes[i];
                    const dayNum = i + 1;
                    const isToday = todayDay === dayNum;
                    return (
                      <TableCell
                        key={i}
                        sx={{
                          bgcolor: isWeekend && forecastMode ? '#4a7a28' : '#5ab031',
                          color: 'white',
                          border: '0px solid #f3f3f3',
                          textAlign: 'center',
                          fontSize: '0.85rem',
                          p: 0.5,
                          ...getTodayColumnSx(isToday, { onGreenHeader: true }),
                        }}
                      >
                        {wd}
                      </TableCell>
                    );
                  })}
                  <TableCell sx={{ 
                    bgcolor: '#5ab031', 
                    color: 'white', 
                    fontWeight: 'normal', 
                    fontSize: '0.85rem', 
                    p: 0.5, 
                    border: '0px solid #f3f3f3', 
                    position: 'sticky', 
                    right: 0, 
                    zIndex: 3, 
                    verticalAlign: 'middle', 
                    textAlign: 'center',
                    minWidth: 60,
                    maxWidth: 80,
                    width: 60,
                    '@media (max-width: 900px)': {
                      position: 'sticky',
                      right: 0,
                      zIndex: 3,
                      boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                    }
                  }} rowSpan={2}>Tot</TableCell>
                </TableRow>
                <TableRow>
                  {/* Tolgo la cella vuota della prima colonna */}
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const isWeekend = weekendIndexes[i];
                    const dayNum = i + 1;
                    const isToday = todayDay === dayNum;
                    return (
                      <TableCell
                        key={i + 1}
                        sx={{
                          bgcolor: isWeekend && forecastMode ? '#4a7a28' : '#5ab031',
                          color: 'white',
                          border: '0px solid #f3f3f3',
                          textAlign: 'center',
                          fontSize: '0.85rem',
                          p: 0.5,
                          ...getTodayColumnSx(isToday, { onGreenHeader: true }),
                        }}
                      >
                        {i + 1}
                      </TableCell>
                    );
                  })}
                  {/* Nessuna TableCell qui per Tot, perché la cella sopra ha rowSpan=2 */}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(pivotData).length === 0 && absenceDays.length > 0 ? (
                  <TableRow
                    key="__absences__"
                    hover
                    sx={{
                      '&:hover': { bgcolor: '#e6f4ea' }
                    }}
                  >
                    <TableCell 
                      className="pivot-user-col"
                      sx={{ 
                        bgcolor: 'white', 
                        color: '#5ab031',
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 2,
                        minWidth: 120, 
                        maxWidth: 160, 
                        width: 140, 
                        p: 0.5, 
                        border: '1px solid #f3f3f3', 
                        borderRight: '2px solid #5ab031', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        textAlign: 'left',
                        fontWeight: 'normal'
                      }}
                    >
                      Absences
                    </TableCell>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const isWeekend = weekendIndexes[i];
                      const day = i + 1;
                      const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const absEntry = absenceDays.find(v => v.date === dateStr);
                      return (
                        <TableCell
                          key={i + 1}
                          sx={{
                            textAlign: 'center',
                            bgcolor: absEntry ? (absEntry.full_day ? '#ffcdd2 !important' : '#fff9c4 !important') : isWeekend ? 'rgba(55,55,55,0.08)' : 'white',
                            color: '#374151',
                            fontSize: '0.7rem',
                            p: 0.5,
                            border: '1px solid #f3f3f3',
                            fontWeight: 'normal',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontVariantNumeric: 'tabular-nums',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: absEntry ? (absEntry.full_day ? '#ef9a9a !important' : '#fff176 !important') : '#aace7c78' }
                          }}
                          onClick={() => navigate(`/daily-tracking?date=${dateStr}`)}
                        >
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ 
                      bgcolor: 'white !important', 
                      color: 'black !important', 
                      textAlign: 'center', 
                      fontSize: '0.7rem', 
                      p: 0.5, 
                      border: '1px solid #f3f3f3', 
                      borderLeft: '2px solid #5ab031', 
                      position: 'sticky', 
                      right: 0, 
                      zIndex: 2,
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      fontWeight: 'bold'
                    }}>
                      {absenceDays.length}
                    </TableCell>
                  </TableRow>
                ) : Object.entries(pivotData).map(([rowKey, days]) => (
                    <TableRow
                      key={rowKey}
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#e6f4ea' }
                      }}
                    >
                      <MuiTooltip title={`${rowKey}`} arrow>
                        <TableCell 
                          className="pivot-user-col"
                          sx={{ 
                            bgcolor: 'white', 
                            color: '#5ab031',
                            position: 'sticky', 
                            left: 0, 
                            zIndex: 2,
                            minWidth: 120, 
                            maxWidth: 160, 
                            width: 140, 
                            p: 0.5, 
                            border: '1px solid #f3f3f3', 
                            borderRight: '2px solid #5ab031', 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontWeight: 'normal',
                            textDecoration: 'underline',
                            '&:hover': {
                              bgcolor: '#e6f4ea',
                              color: '#4a9b2a',
                              fontWeight: 'bold'
                            },
                            '@media (max-width: 900px)': {
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          {rowKey}
                        </TableCell>
                      </MuiTooltip>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const obj = days[i + 1];
                        const isWeekend = weekendIndexes[i];
                        const day = i + 1;
                        const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const absEntry = absenceDays.find(v => v.date === dateStr);
                        const projectId = obj?.project_id || projectMeta[rowKey]?.project_id;
                        const isToday = todayDay === day;
                        return (
                          <TableCell
                            key={i + 1}
                            sx={getCellSx(dateStr, absEntry, isWeekend, rowKey, day, projectId, isToday)}
                            onClick={() => handleCellClick(dateStr, rowKey, day, projectId)}
                          >
                            {renderPivotCellContent(obj, dateStr, rowKey, day, projectId)}
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ 
                        bgcolor: 'white !important', 
                        color: 'black !important', 
                        textAlign: 'center', 
                        fontSize: '0.7rem', 
                        p: 0.5, 
                        border: '1px solid #f3f3f3', 
                        borderLeft: '2px solid #5ab031', 
                        position: 'sticky', 
                        right: 0, 
                        zIndex: 2,
                        minWidth: 60,
                        maxWidth: 80,
                        width: 60,
                        fontWeight: 'bold',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          right: 0,
                          zIndex: 2,
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }}>{rowTotals[rowKey]}</TableCell>
                    </TableRow>
                  ))}
                {/* Riga totale per giorno (ripristinata) */}
                <TableRow sx={{ bgcolor: '#248b7e' }}>
                  <TableCell 
                      sx={{  
                        bgcolor: 'white', 
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 2, 
                        minWidth: 120, 
                        maxWidth: 160, 
                        width: 140, 
                        p: 0.5, 
                        border: '1px solid #f3f3f3', 
                        borderRight: '2px solid #5ab031', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        textAlign: 'right', 
                        borderTop: '2px solid #5ab031',
                        '@media (max-width: 900px)': {
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }
                      }} 
                    className="pivot-user-col"
                  >
                    Tot
                  </TableCell>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const dayTotal = dayTotals[i+1];
                    const isOver8Hours = dayTotal > 8;
                    const isToday = todayDay === i + 1;
                    
                    return (
                      <Tooltip 
                        key={i+1}
                        title={isOver8Hours ? `Warning: ${dayTotal} hours exceeds the recommended 8-hour daily limit` : `${dayTotal} hours`}
                        arrow
                      >
                        <TableCell 
                          sx={{ 
                            bgcolor: isOver8Hours ? '#fdeded' : 'white !important', 
                            color: 'black !important', 
                            textAlign: 'center', 
                            fontSize: '0.7rem', 
                            p: 0.5, 
                            border: '1px solid #f3f3f3', 
                            borderTop: '2px solid #5ab031', 
                            fontWeight: 'bold',
                            ...getTodayColumnSx(isToday),
                          }}
                        >
                          {dayTotal}
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                    <TableCell sx={{ 
                      bgcolor: 'white !important', 
                      color: 'black !important', 
                      textAlign: 'center', 
                      fontSize: '0.7rem', 
                      p: 0.5, 
                      border: '1px solid #f3f3f3', 
                      borderLeft: '2px solid #5ab031', 
                      borderTop: '2px solid #5ab031', 
                      position: 'sticky', 
                      right: 0, 
                      zIndex: 2,
                      minWidth: 60,
                      maxWidth: 80,
                      width: 60,
                      fontWeight: 'bold',
                      '@media (max-width: 900px)': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 2,
                        boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                      }
                    }}>{grandTotal}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* Note about effort in hours */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mt: 1, 
          pr: 1 
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#666', 
              fontSize: '0.75rem',
              fontStyle: 'italic'
            }}
          >
            * effort in hours{forecastMode ? ' — unsaved changes in blue until you save' : ''}
          </Typography>
        </Box>

        {forecastMode && hasPendingForecastChanges && (
          <Paper
            elevation={6}
            sx={{
              position: 'sticky',
              bottom: 16,
              mt: 2,
              mx: 'auto',
              maxWidth: 560,
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              border: '1px solid #90caf9',
              bgcolor: '#e3f2fd',
              zIndex: 10,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Chip
                size="small"
                label={`${pendingForecastChangesCount} unsaved`}
                color="primary"
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleDiscardForecastDrafts} disabled={savingForecasts}>
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={handleSaveAllForecasts}
                disabled={savingForecasts || pivotLoading}
              >
                Save Forecast
              </Button>
            </Box>
          </Paper>
        )}

        <Dialog open={unsavedConfirmOpen} onClose={() => setUnsavedConfirmOpen(false)}>
          <DialogTitle>Unsaved forecast changes</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              You have {pendingForecastChangesCount} unsaved change{pendingForecastChangesCount === 1 ? '' : 's'}.
              Discard them and continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnsavedConfirmOpen(false)}>Keep editing</Button>
            <Button onClick={handleConfirmDiscardUnsaved} color="warning" variant="contained">
              Discard & continue
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
} 